import type { IUser, IUpdateUser } from 'src/types/user';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchUser, updateUser, fetchCurrentUser, updateCurrentUser } from 'src/api/testelopeUser';

export const testelopeUserKeys = {
  all: ['testelopeUser'] as const,
  current: () => [...testelopeUserKeys.all, 'current'] as const,
  detail: (userId: number) => [...testelopeUserKeys.all, 'detail', userId] as const,
};

export function useCurrentTestelopeUserQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: testelopeUserKeys.current(),
    queryFn: (): Promise<IUser> => fetchCurrentUser(),
    enabled,
  });
}

export function useTestelopeUserQuery(userId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: testelopeUserKeys.detail(userId),
    queryFn: (): Promise<IUser> => fetchUser(userId),
    enabled: enabled && Number.isFinite(userId) && userId > 0,
    retry: false,
  });
}

export function useUpdateCurrentTestelopeUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IUpdateUser) => updateCurrentUser(payload),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData<IUser | undefined>(testelopeUserKeys.current(), updatedUser);
    },
  });
}

export function useUpdateTestelopeUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, payload }: { userId: number; payload: IUpdateUser }) =>
      updateUser(userId, payload),
    onSuccess: (updatedUser, variables) => {
      queryClient.setQueryData<IUser | undefined>(
        testelopeUserKeys.detail(variables.userId),
        updatedUser
      );
      queryClient.invalidateQueries({ queryKey: testelopeUserKeys.current() });
    },
  });
}
